import { useQueryClient } from '@tanstack/react-query';

import { setProcessoFavorito, type Processo } from '@/api/client';
import type { ProcessoInterno } from '@/api/processos';
import { useNotification } from '@/context/NotificationProvider';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import { notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

interface Variaveis {
  id: string;
  favorito: boolean;
}

/**
 * Contador de toques por processo, no MÓDULO: a mesma estrela aparece em mais de
 * um lugar (linha da tabela, card mobile, cabeçalho dos autos), cada um com a sua
 * instância do hook — o "toque mais recente" tem de ser um só para o processo.
 */
const ultimoToque = new Map<string, number>();

/** Prefixo das leituras do processo pelo número (cabeçalho dos autos). */
const BY_NUMERO = ['processos', 'by-numero'] as const;

/**
 * "Acompanho de perto" — marca/desmarca o processo, OTIMISTA com volta.
 *
 * A estrela muda no toque (esperar a rede por um bit faz a interface parecer
 * quebrada); recusado pelo servidor, ela volta e o erro aparece — ficar marcada
 * mentindo é pior que o atraso. Manda o VALOR que a tela quer (PUT idempotente),
 * nunca um toggle: dois toques rápidos ou duas abas não invertem a estrela.
 *
 * Toques rápidos: só o ÚLTIMO toque de cada processo decide a estrela. A
 * resposta (ou o erro) de um toque já superado não mexe na tela — senão um erro
 * atrasado desfaria o toque mais novo. A resposta do último toque reconcilia a
 * estrela com o que o servidor gravou.
 *
 * Offline, a ação vai para o outbox como um PUT comum, num grupo por processo
 * (FIFO): marcar e desmarcar sem rede chegam ao servidor na ordem em que foram
 * feitos, mesmo se a primeira tentativa de um deles falhar.
 */
export function useFavoritoProcesso() {
  const queryClient = useQueryClient();
  const { showError } = useNotification();

  const aplicar = (id: string, favorito: boolean) => {
    queryClient.setQueryData<Processo[]>([...queryKeys.processos.all], (lista) =>
      lista?.map((p) => (p.id === id ? { ...p, favorito } : p)),
    );
    // O acervo filtrado pelos prazos do painel é outra lista (Bloco F), e a visão do
    // escritório também (o próprio acervo + o dos colegas, PLANO_COMPARTILHAMENTO_ESCRITORIO).
    for (const outra of [['processos', 'vencimento'], [...queryKeys.processos.all, 'visao']]) {
      queryClient.setQueriesData<Processo[]>({ queryKey: outra }, (lista) =>
        lista?.map((p) => (p.id === id ? { ...p, favorito } : p)),
      );
    }
    queryClient.setQueriesData<ProcessoInterno>({ queryKey: [...BY_NUMERO] }, (p) =>
      p && p.id === id ? { ...p, favorito } : p,
    );
  };

  const mutation = useOfflineMutation<Processo, Variaveis>({
    mutationFn: ({ id, favorito }) => setProcessoFavorito(id, favorito),
    outbox: {
      type: 'processo_editar',
      getPayload: ({ id, favorito }) => ({
        method: 'PUT',
        url: `/processos/${id}/favorito`,
        body: { favorito },
        grupo: `favorito:${id}`,
      }),
      getDescription: ({ favorito }) =>
        favorito ? 'Acompanhar processo de perto' : 'Deixar de acompanhar processo de perto',
    },
  });

  const definir = async (id: string, favorito: boolean) => {
    // Um refetch em voo traria o valor antigo por cima do otimista.
    await Promise.all([
      queryClient.cancelQueries({ queryKey: [...queryKeys.processos.all], exact: true }),
      queryClient.cancelQueries({ queryKey: [...queryKeys.processos.all, 'visao'] }),
      queryClient.cancelQueries({ queryKey: [...BY_NUMERO] }),
    ]);
    const toque = (ultimoToque.get(id) ?? 0) + 1;
    ultimoToque.set(id, toque);
    const ehOUltimo = () => ultimoToque.get(id) === toque;

    aplicar(id, favorito);
    try {
      const resposta = await mutation.mutateAsync({ id, favorito });
      // Online, o servidor devolve o processo: a estrela fica com o que foi gravado.
      if (ehOUltimo() && typeof resposta?.favorito === 'boolean') aplicar(id, resposta.favorito);
    } catch (err) {
      if (!ehOUltimo()) return;
      aplicar(id, !favorito);
      notifyMutationError(err, showError);
    }
  };

  return { definir };
}
